import { Banknote } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import IbottaOfferChip from "./IbottaOfferChip";
import { formatPrice, getCashbackAmount, getIbottaOffers, getIsNewProduct, getOfferLabel, getOfferType, getPriceChange, getStockChange } from "./ProductItem.utils";
import type { IbottaOffer } from "../types/Product";

export type ProductStatusKind = "new" | "lowest" | "price_down" | "price_up" | "stock_up" | "stock_down";

interface StatusInput {
  firstInserted?: number;
  storePrice?: number[];
  salesFloor?: number[];
}

const isAtLowest = (prices?: number[]) => {
  if (!prices || prices.length < 3) return false;
  const current = prices[prices.length - 1];
  if (current <= 0) return false;
  const recent = prices.slice(Math.max(0, prices.length - 4), prices.length - 1);
  return recent.every((p) => p > current) && !prices.slice(0, -1).some((p) => p > 0 && p < current);
};

/**
 * The list row's status tag with the app's precedence: new within 24h, then
 * all-time low, then the last price move, then the last stock move.
 */
export function getProductStatus(product: StatusInput, priceHistory: number[] | undefined = product.storePrice): ProductStatusKind | null {
  if (getIsNewProduct(product.firstInserted)) return "new";
  if (isAtLowest(product.storePrice)) return "lowest";
  const price = getPriceChange(priceHistory);
  if (price === "down") return "price_down";
  if (price === "up") return "price_up";
  const stock = getStockChange(product.salesFloor);
  if (stock === "up") return "stock_up";
  if (stock === "down") return "stock_down";
  return null;
}

const STATUS_CLASS: Record<ProductStatusKind, string> = {
  new: "bg-emerald-500",
  lowest: "bg-violet-500",
  price_down: "bg-green-500",
  price_up: "bg-rose-500",
  stock_up: "bg-sky-500",
  stock_down: "bg-orange-500",
};

/** Small solid status tag (NEW / LOWEST / PRICE ↓ ...); nothing when the product has no status. */
export function ProductStatusBadge({ kind, className = "" }: { kind: ProductStatusKind | null; className?: string }) {
  const { t } = useTranslation();
  if (!kind) return null;
  const label = {
    new: t("products.badgeNew"),
    lowest: t("topFeed.badgeLowest"),
    price_down: t("products.badgePriceDown"),
    price_up: t("products.badgePriceUp"),
    stock_up: t("products.badgeStockUp"),
    stock_down: t("products.badgeStockDown"),
  }[kind];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm ${STATUS_CLASS[kind]} ${className}`}>
      {label}
    </span>
  );
}

interface OfferInput {
  cashbackAmount?: number;
  offerType?: string;
  offerText?: string;
  ibottaOffers?: IbottaOffer[] | null;
}

export const hasProductOffers = (product: OfferInput) =>
  getCashbackAmount(product) > 0 || getIbottaOffers(product).length > 0 || Boolean(getOfferType(product));

/** Cash back, Ibotta and BOGO / mix-and-match chips, the same set the list row shows. */
export function ProductOfferChips({
  product,
  className = "",
  hideOffer = false,
}: {
  product: OfferInput;
  className?: string;
  /** Skip the BOGO / mix-and-match chip when the price row already headlines it. */
  hideOffer?: boolean;
}) {
  const { t } = useTranslation();
  const cashback = getCashbackAmount(product);
  const ibottaOffers = getIbottaOffers(product);
  const offerType = getOfferType(product);
  const offerLabel = hideOffer ? "" : getOfferLabel(product);
  if (cashback <= 0 && ibottaOffers.length === 0 && !offerLabel) return null;
  return (
    <div className={`flex min-w-0 flex-wrap items-center gap-1 ${className}`}>
      {cashback > 0 && (
        <span className="inline-flex items-center gap-0.5 rounded-md bg-teal-500/10 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
          <Banknote className="h-3 w-3" />
          {t("products.cashbackAmount", { amount: formatPrice(cashback) })}
        </span>
      )}
      <IbottaOfferChip offers={ibottaOffers} />
      {offerLabel && (
        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${
          offerType === "bogo"
            ? "bg-indigo-500/10 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
            : "bg-sky-500/10 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
        }`}>
          {offerLabel}
        </span>
      )}
    </div>
  );
}
